// Copy-paste deployment for a worker on ANOTHER host.
//
// The compose file itself is not generated here any more. It comes from
// @lib/remote-compose.ts, which the SSH deploy also renders from — a second copy
// of that generator is how the documented manual procedure stops describing what
// the automated one does. This component is the form around it: the auto-update
// toggle, the token, and the warning when no public release origin exists.
//
// Two things make this more than a `docker run` line:
//
//   Release origin — a remote worker cannot resolve the manager's docker
//   container name, so it needs a publicly reachable origin. We surface that
//   requirement instead of letting the worker fail at bundle-download time.
//
//   Auto-update — a container cannot pull and recreate itself, so rolling a
//   remote worker needs a small updater-agent sidecar beside it. Toggling it on
//   emits that service and mints the shared token, ready to paste into the host's
//   updater-agent settings.

import {
	AGENT_PORT,
	mintAgentToken,
	type RemoteWorkerComposeInfo,
	renderRemoteWorkerCompose,
} from "@lib/remote-compose.ts";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldLabel,
} from "@veil/ui/components/field";
import { Switch } from "@veil/ui/components/switch";
import Copy from "lucide-react/dist/esm/icons/copy";
import RefreshCw from "lucide-react/dist/esm/icons/refresh-cw";
import { useId, useState } from "react";
import { toast } from "sonner";

/**
 * What the generator needs to know about this cluster. Aliased rather than
 * redeclared so a field added server-side cannot be quietly missing here; the
 * shape comes from GET /api/system/provision-info.
 */
export type RemoteWorkerInfo = RemoteWorkerComposeInfo;

export function RemoteWorkerSnippet({
	info,
	hostname,
	onAgentConfigured,
}: {
	info: RemoteWorkerInfo;
	hostname: string;
	/**
	 * Called with the agent credentials so a caller (the deploy wizard) can
	 * prefill the fleet-host record it is about to create.
	 */
	onAgentConfigured?: (agent: { token: string; port: number } | null) => void;
}) {
	const toggleId = useId();
	const [autoUpdate, setAutoUpdate] = useState(false);
	// Lazy: only generate once, and not at all until the component mounts.
	const [token, setToken] = useState(mintAgentToken);

	// Derived during render. The generator is pure string building, so memoising
	// it would cost a dependency list for less work than it saves.
	const snippet = renderRemoteWorkerCompose({
		info,
		hostname,
		autoUpdate,
		token,
	});

	const setAutoUpdateAndNotify = (next: boolean) => {
		setAutoUpdate(next);
		onAgentConfigured?.(next ? { token, port: AGENT_PORT } : null);
	};

	const regenerate = () => {
		const next = mintAgentToken();
		setToken(next);
		if (autoUpdate) onAgentConfigured?.({ token: next, port: AGENT_PORT });
	};

	const copy = () => {
		navigator.clipboard
			.writeText(snippet)
			.then(() => toast.success("Copied compose file"))
			.catch(() => toast.error("Copy failed"));
	};

	return (
		<div className="space-y-3">
			<Field orientation="horizontal" className="rounded-md border p-3">
				<Switch
					id={toggleId}
					checked={autoUpdate}
					onCheckedChange={setAutoUpdateAndNotify}
				/>
				<FieldContent>
					<FieldLabel htmlFor={toggleId}>Enable one-click updates</FieldLabel>
					<FieldDescription>
						Adds an updater-agent beside the worker so you can roll it to a new
						image from Infrastructure &gt; Updates. Without it, updating this
						worker means running <code>docker compose pull</code> on that host
						yourself.
					</FieldDescription>
				</FieldContent>
			</Field>

			{autoUpdate ? (
				<div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed p-3 text-xs">
					<span className="text-muted-foreground">Agent token</span>
					<code className="min-w-0 flex-1 truncate font-mono">{token}</code>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={regenerate}
						title="Generate a different token"
					>
						<RefreshCw className="size-3" /> New
					</Button>
				</div>
			) : null}

			{info.releaseOrigin === null ? (
				<p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
					<Badge variant="warning" className="mr-2">
						Action needed
					</Badge>
					No public release origin is configured, so the placeholder below will
					not work. Set the cluster's release hostname first — a worker on
					another host cannot reach this manager by its container name.
				</p>
			) : null}

			<div className="relative">
				<pre className="max-h-72 overflow-auto rounded-md bg-muted/50 p-3 font-mono text-[11px] leading-relaxed">
					{snippet}
				</pre>
				<Button
					type="button"
					variant="secondary"
					size="sm"
					className="absolute right-2 top-2"
					onClick={copy}
				>
					<Copy className="size-3" /> Copy
				</Button>
			</div>
		</div>
	);
}
